// ── WHICH STAGES WAS A CLAIM ACTUALLY PAID ON ────────────────────────────────
//
// Kevin, 2026-10-01, on Verratti Farms' CY2026 card: *"it says verrattis claim was paid
// for both Prevent Plant and harvest loss. This i believe is untrue, I think the payment
// was for Prevent Plant only; they have an active harvest claim for silage appraisals but
// they have not been paid on a harvest claim for 2026."*
//
// He is right, and the cause is a rule this repo had already established and applied one
// record short. CLAUDE.md, on the Sullivan Farms county defect: *"A CLAIM'S CROP AND
// COUNTY COME FROM WHERE THE MONEY WENT — P21, NOT P20X"* — and then, in as many words:
// **"Stages still union; only cids don't."**
//
// MEASURED 2026-10-01. Verratti claim 00000081, paid 2026-08-12, $46,233 check + $21,544
// premium credit + $60 admin fee. `indemnities.claim_stages` reads **"HAR,PP"**. Every one
// of its **nineteen** P21 worksheet lines is stage **`P2`** — prevent plant. There is no
// harvest line at all. The harvest stage was OPENED by the adjuster (the silage appraisal
// claim still in adjustment) and has never been paid.
//
// P20X is the adjuster's NOTICE record: it carries the stages a claim was opened under.
// P21 carries the money. A claim can be opened under three stages and paid on one.
//
// ⚠ SO THE ANSWER IS TWO SETS, NOT A NARROWER ONE. Dropping the unpaid stage would hide
// the open half of the claim — which is the thing the grower is actually waiting on, and
// exactly what Kevin was describing. "Paid on prevent plant; harvest open, unpaid" is the
// true sentence and it is strictly more informative than either label alone.
//
// ⚠ AND THE P20X FALLBACK IS LOAD-BEARING — do not narrow it away. CLAUDE.md records that
// 95 open claims and 60 R&H released claims have NO P21 lines whatsoever; for those the
// paid stages are simply unknown, and the stored union must stand exactly as it does
// today. Unknown is not "nothing was paid".

"use strict";

// P2/PF/PT are prevent-plant buy-up tiers of one stage; UH is the production-to-count
// offset against a gross H. Grouping here so "HAR" from a notice and "H" from a worksheet
// are recognised as the same stage rather than read as two.
//
// ⚠ THIS MIRRORS `CLAIM_STAGE_GROUP` IN App.jsx DELIBERATELY AND THE CHECKER ASSERTS THEY
// AGREE. It cannot import it — App.jsx exports nothing — and a second copy of a grouping
// is how one surface comes to call two stages the same and the surface beside it does not.
// The prevent-plant codes come from the ONE shared set (src/claims/ppStages.js).
const { PP_STAGES } = require("./ppStages");
const STAGE_GROUP = Object.freeze({
  ...Object.fromEntries(PP_STAGES.map((c) => [c, "PP"])),
  HAR: "H", H: "H", UH: "H",
  REP: "R", R: "R",
});
const grp = (c) => {
  const k = String(c == null ? "" : c).trim().toUpperCase();
  return STAGE_GROUP[k] || k;
};

/**
 * claimPaidStages(storedCodes, moneyCodes)
 *   storedCodes — indemnities.claim_stages, already split (the P20X notice union)
 *   moneyCodes  — stage codes on this claim's P21 worksheet lines; EMPTY means no P21
 *
 * -> { paid, openOnly, basis }
 *      paid     : the codes to LABEL the payment with
 *      openOnly : stages the notice opened that the money never reached
 *      basis    : "p21" when the money decided, "p20x" when it could not
 */
function claimPaidStages(storedCodes = [], moneyCodes = []) {
  const stored = (storedCodes || []).map((c) => String(c || "").trim()).filter(Boolean);
  const money = (moneyCodes || []).map((c) => String(c || "").trim()).filter(Boolean);
  // No P21 → the paid stages are UNKNOWN, and the stored union stands. Returning an
  // empty `paid` here would read as "paid on nothing" on every open claim in the book.
  if (!money.length) return { paid: stored, openOnly: [], basis: "p20x" };
  const paidGroups = new Set(money.map(grp));
  return {
    paid: money,
    openOnly: stored.filter((c) => !paidGroups.has(grp(c))),
    basis: "p21",
  };
}

module.exports = { claimPaidStages, STAGE_GROUP };
